import { useEffect, useState } from 'react';
import { CheckCircle, Clock3, Loader2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

type ResultState = 'confirming' | 'success' | 'pending' | 'failed';
const POLL_MS = 2000;
const TIMEOUT_MS = 60_000;

export default function BankerBillingResult() {
  const [state, setState] = useState<ResultState>('confirming');

  useEffect(() => {
    let stopped = false;
    const started = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const result = await api.getBankerBillingStatus();
        if (stopped) return;
        if (result.investment_banking.active) {
          setState('success');
          return;
        }
        if (['overdue_disabled', 'cancelled'].includes(result.investment_banking.state)) {
          setState('failed');
          return;
        }
      } catch {
        // Keep polling through transient provider/webhook delays.
      }
      if (!stopped && Date.now() - started < TIMEOUT_MS) timer = setTimeout(poll, POLL_MS);
      else if (!stopped) setState('pending');
    };
    poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, []);

  const content = {
    confirming: { icon: <Loader2 className="h-10 w-10 animate-spin text-teal-300" />, title: 'Confirming your payment...', body: 'We are waiting for the signed payment event. This can take a moment.' },
    success: { icon: <CheckCircle className="h-10 w-10 text-emerald-300" />, title: 'Banker access confirmed', body: 'Your Investment Banker add-on is ready.' },
    pending: { icon: <Clock3 className="h-10 w-10 text-amber-300" />, title: 'Payment still pending', body: 'We have not received a signed confirmation yet. You can return to billing and check again later.' },
    failed: { icon: <XCircle className="h-10 w-10 text-red-300" />, title: 'Banker access was not confirmed', body: 'Please try checkout again or contact support if you were charged.' },
  }[state];

  return <main className="min-h-[60vh] p-6 flex items-center justify-center"><section className="max-w-lg rounded-2xl border border-slate-700 bg-slate-800 p-8 text-center shadow-xl" aria-live="polite"><div className="flex justify-center">{content.icon}</div><h1 className="mt-5 text-2xl font-bold text-white">{content.title}</h1><p className="mt-3 text-slate-300">{content.body}</p><Link to="/subscription" className="mt-7 inline-flex min-h-11 items-center rounded-lg bg-teal-500 px-5 py-3 font-semibold text-slate-950 hover:bg-teal-400">Manage billing</Link></section></main>;
}
